/**
 * DSD 테이블 셀 + 텍스트 블록 컴포넌트 (Composite Pattern)
 *
 * Figma: Design System 0.1 > IXD-DSD Reconciliation Table
 *
 * - DsdTable.Header: DSD 헤더 셀 (ColumnHeader·RowHeader)
 * - DsdTable.Cell: DSD 본문 셀 (matchResult)
 * - DsdTable.NarrativeBlock: 텍스트 문단 블록 (matchResult)
 */

import { forwardRef } from "react";
import { TriangleAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import { cva } from "class-variance-authority";
import { DecorateInteraction } from "@/toggle-ui-kit/decorate/interaction/decorate-interaction";
import { SumDiffChip } from "@/toggle-ui-kit/dsd-table";

// ============================================
// 태깅 경고 마커 — 헤더가 가리키는 위치가 매칭 정보에 없는 셀에 표시
// ============================================

/**
 * 셀 우상단 경고 아이콘. 셀 배경색(match 상태색)과 무관하게 인식되도록 amber 로
 * 채우고, z-index 로 콘텐츠/decorate 위에 띄운다. 정보 표시 전용(클릭 통과).
 */
const TagWarningMarker = () => (
  <span
    className="pointer-events-none absolute right-0.5 top-0.5 z-[2] text-amber-900"
    title="헤더가 가리키는 위치가 매칭 정보에 없습니다."
  >
    <TriangleAlert className="h-3.5 w-3.5 fill-amber-400" strokeWidth={2} />
  </span>
);

// ============================================
// 모서리 자리 — 셀 오른쪽 위에 겹쳐 띄우는 표시(IXD 위치 확인 점 등)
// ============================================

/**
 * 셀 흐름에 넣지 않고 겹쳐 띄운다 — 흐름에 넣으면 셀 높이·너비가 바뀐다. 영역 검토
 * 모드에서는 global.css 가 `dsd-counterpart-label` 을 숨긴다(기간 이름표와 자리가 겹친다).
 */
const CornerSlot = ({ children }: { children: React.ReactNode }) => (
  <div className="dsd-counterpart-label absolute right-0.5 top-0.5 z-[2] flex items-center gap-0.5">
    {children}
  </div>
);

// ============================================
// 공통 타입
// ============================================

export type DsdMatchResult =
  | "default"
  | "missing-in-ixd"
  | "match"
  | "misplaced-in-ixd"
  | "sign-reversed"
  | "rounded"
  | "should-tag";

export type DsdHeaderType = "column" | "row";

// ============================================
// matchResult → 배경색 매핑
// ============================================

const matchResultBgMap: Record<DsdMatchResult, string> = {
  default: "bg-background-base",
  "missing-in-ixd": "bg-background-base",
  match: "bg-green-200",
  "misplaced-in-ixd": "bg-rose-200",
  "sign-reversed": "bg-amber-200",
  rounded: "bg-yellow-200",
  "should-tag": "bg-red-100",
};

// ============================================
// DsdHeaderCell
// ============================================

export interface DsdHeaderCellProps
  extends React.ThHTMLAttributes<HTMLTableCellElement> {
  /** 헤더 타입: column(neutral-200) / row(white) */
  type?: DsdHeaderType;
  /** 대사 결과 — default 외의 값이면 type 기본 배경을 덮어쓰고 상태 색을 표시 */
  matchResult?: DsdMatchResult;
  /** 태깅 경고 — 헤더가 가리키는 위치가 매칭 정보에 없을 때 우상단 마커 표시 */
  warning?: boolean;
  /** 셀 오른쪽 위에 겹쳐 띄울 표시 — 셀 크기에 영향을 주지 않는다 */
  cornerSlot?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

const thVariants = cva(
  "box-border relative min-w-[80px] max-w-[200px] p-0 border-b border-r border-line-normal rounded-none before:z-[1]",
  {
    variants: {
      type: {
        column: "bg-neutral-200",
        row: "bg-background-base",
      },
    },
  },
);

const DsdHeaderCell = forwardRef<HTMLTableCellElement, DsdHeaderCellProps>(
  (
    {
      type = "column",
      matchResult,
      warning,
      cornerSlot,
      children,
      className,
      ...rest
    },
    ref,
  ) => {
    const matchBg =
      matchResult && matchResult !== "default"
        ? matchResultBgMap[matchResult]
        : undefined;
    return (
      <DecorateInteraction asChild variant="strong" color="neutral">
        <th
          ref={ref}
          className={cn(thVariants({ type }), matchBg, className)}
          {...rest}
        >
          {warning && <TagWarningMarker />}
          {cornerSlot ? <CornerSlot>{cornerSlot}</CornerSlot> : null}
          <div className="relative flex items-center gap-1 p-2 caption-2-medium">
            <span className="min-w-0 flex-1 text-label-strong">{children}</span>
          </div>
        </th>
      </DecorateInteraction>
    );
  },
);

DsdHeaderCell.displayName = "DsdTable.Header";

// ============================================
// DsdCell
// ============================================

export interface DsdCellProps
  extends React.TdHTMLAttributes<HTMLTableCellElement> {
  /** 대사 결과 */
  matchResult?: DsdMatchResult;
  /** 단수차이 칩 라벨 (e.g. "-1", "+2") — rounded 셀에 상시 표시 */
  sumDiffLabel?: string;
  /** 태깅 경고 — 헤더가 가리키는 위치가 매칭 정보에 없을 때 우상단 마커 표시 */
  warning?: boolean;
  /** 셀 오른쪽 위에 겹쳐 띄울 표시 — 셀 크기에 영향을 주지 않는다 */
  cornerSlot?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

const cellBaseClass =
  "relative min-w-[80px] p-0 border-b border-r border-line-normal rounded-none before:z-[1]";

const DsdCell = forwardRef<HTMLTableCellElement, DsdCellProps>(
  (
    {
      matchResult = "default",
      sumDiffLabel,
      warning,
      cornerSlot,
      children,
      className,
      ...rest
    },
    ref,
  ) => {
    return (
      <DecorateInteraction asChild variant="light" color="neutral">
        <td
          ref={ref}
          className={cn(
            cellBaseClass,
            matchResultBgMap[matchResult],
            className,
          )}
          {...rest}
        >
          {warning && <TagWarningMarker />}
          {cornerSlot ? <CornerSlot>{cornerSlot}</CornerSlot> : null}
          <div
            className={cn(
              "flex items-center gap-1 rounded-none p-2 caption-2-medium",
              matchResult === "missing-in-ixd"
                ? "text-label-alternative"
                : "text-label-normal",
            )}
          >
            {sumDiffLabel && (
              <SumDiffChip variant="dark">{sumDiffLabel}</SumDiffChip>
            )}
            <span className="min-w-0 flex-1">{children}</span>
          </div>
        </td>
      </DecorateInteraction>
    );
  },
);

DsdCell.displayName = "DsdTable.Cell";

// ============================================
// DsdNarrativeBlock
// ============================================

export interface DsdNarrativeBlockProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** 대사 결과 */
  matchResult?: DsdMatchResult;
  children?: React.ReactNode;
  className?: string;
}

const DsdNarrativeBlock = ({
  matchResult = "default",
  children,
  className,
  ...rest
}: DsdNarrativeBlockProps) => {
  const bgClass = matchResultBgMap[matchResult];

  return (
    <div className={cn("relative max-w-[540px]", bgClass, className)} {...rest}>
      <DecorateInteraction asChild variant="light" color="neutral">
        <div
          className={cn(
            "rounded-none px-2 py-1 label-2-medium",
            matchResult === "missing-in-ixd"
              ? "text-label-alternative"
              : "text-label-normal",
          )}
        >
          {children}
        </div>
      </DecorateInteraction>
    </div>
  );
};

DsdNarrativeBlock.displayName = "DsdTable.NarrativeBlock";

// ============================================
// Composite Export
// ============================================

export const DsdTable = {
  Header: DsdHeaderCell,
  Cell: DsdCell,
  NarrativeBlock: DsdNarrativeBlock,
};

export default DsdTable;
